import React, { useEffect } from 'react'

import AmuseBoostButton from './AmuseBoostButton'
import AmuseBoostModal from './AmuseBoostModal'

import BuyBonusButton from '@components/BuyBonus/BuyBonusButton'
import BuyBonusModal from '@components/BuyBonus/BuyBonusModal'
import DoubleChanceButton from '@components/DoubleChanceButton/DoubleChanceButton'

import { useAmuseBoostStore, useBuyBonusStore, useConfigurationStore, useDoubleChanceStore } from '@stores'
import { useLoadAtlasesWhenGameSettingsReady } from '@hooks'
import { getBuyBonusAtlasPaths } from '@utils/gameSettings'

import {
    BuyBonusToggleAdditionalFeatureMessage,
    BuyBonusButtonClickedMessage,
    BuyBonusRejectedMessage,
    CollectClickedMessage,
    GameUIMessageScope,
    GameUIMessageType,
    ChangeOpacityComponents,
    ChangeOpacityMessagePrefix,
} from 'game-ui-types'
import { FeatureGroup } from '@constants/buyBonus'

const AmuseBoost: React.FC = () => {
    const showModal = useBuyBonusStore((s) => s.showModal)
    const setShowModal = useBuyBonusStore((s) => s.setShowModal)
    const loadAtlasesFromPath = useBuyBonusStore((s) => s.loadAtlasesFromPath)
    const setMultiChoiceToggle = useBuyBonusStore((s) => s.setMultiChoiceToggle)
    const isMultiChoiceToggled = useBuyBonusStore((s) => s.isMultiChoiceToggled)
    const updateCurrentFeatureMultiplier = useBuyBonusStore((s) => s.updateCurrentFeatureMultiplier)

    const buyBonusConfig = useBuyBonusStore((s) => s.buyBonusConfig)
    const setHasChancePlus = useBuyBonusStore((state) => state.setHasChancePlus)
    const { features } = buyBonusConfig
    const hasChancePlus = features.filter((feature) => feature.featureGroup === FeatureGroup.ChancePlus).length > 0

    useEffect(() => {
        setHasChancePlus(hasChancePlus)
    }, [hasChancePlus])

    const hasBuyBonus = useConfigurationStore((s) => s.hasBuyBonus)
    const hasDoubleChance = useConfigurationStore((s) => s.hasDoubleChance)

    const isAmuseBoostModalOpen = useAmuseBoostStore((s) => s.isAmuseBoostModalOpen)
    const setIsAmuseBoostModalOpen = useAmuseBoostStore((s) => s.setIsAmuseBoostModalOpen)

    const isDoubleChanceActivated = useDoubleChanceStore((s) => s.isDoubleChanceActivated)

    useLoadAtlasesWhenGameSettingsReady(() => (hasBuyBonus ? getBuyBonusAtlasPaths() : []), loadAtlasesFromPath, [
        hasBuyBonus,
    ])

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const { type, scope } = event.data

            if (scope !== GameUIMessageScope.BUY_BONUS) {
                return
            }

            switch (type) {
                case GameUIMessageType.BUY_BONUS_TOGGLE_ADDITIONAL_FEATURE: {
                    const data = event.data as BuyBonusToggleAdditionalFeatureMessage

                    updateCurrentFeatureMultiplier(data.payload.multiplier)
                    break
                }
            }
        }

        window.uiBus.addEventListener('message', handleMessage, false)

        return () => {
            window.uiBus.removeEventListener('message', handleMessage, false)
        }
    }, [updateCurrentFeatureMultiplier])

    const handleBuyBonusButtonClicked = () => {
        const collectClickedMessage: CollectClickedMessage = {
            type: GameUIMessageType.COLLECT_CLICKED,
        }

        window.uiBus.postMessage(collectClickedMessage)

        if (isMultiChoiceToggled) {
            setMultiChoiceToggle(false)
            updateCurrentFeatureMultiplier(1)

            const buyBonusRejectedMessage: BuyBonusRejectedMessage = {
                type: GameUIMessageType.BUY_BONUS_REJECTED,
            }

            window.uiBus.postMessage(buyBonusRejectedMessage)

            window.uiBus.postMessage({
                type: `${ChangeOpacityMessagePrefix}${ChangeOpacityComponents.BUY_BONUS_BUTTON}`,
                payload: {
                    opacity: 1,
                    isInteractive: true,
                },
            })

            return
        }

        setShowModal(true)

        const buyBonusButtonClickedMessage: BuyBonusButtonClickedMessage = {
            type: GameUIMessageType.BUY_BONUS_BUTTON_CLICKED,
            scope: GameUIMessageScope.BUY_BONUS,
        }

        window.uiBus.postMessage(buyBonusButtonClickedMessage)
    }

    const handleSelectBuyBonusFromAmuseBoost = () => {
        setIsAmuseBoostModalOpen(false)

        handleBuyBonusButtonClicked()
    }

    const handleAmuseBoostClick = () => {
        if (isAmuseBoostModalOpen) {
            setIsAmuseBoostModalOpen(false)

            return
        }

        if (hasChancePlus) {
            setIsAmuseBoostModalOpen(false)
            handleBuyBonusButtonClicked()

            return
        }

        window.uiBus.postMessage({
            type: 'changeOpacityDoubleChanceButton',
            payload: {
                opacity: 1,
                isInteractive: true,
            },
        })

        window.uiBus.postMessage({
            type: 'changeOpacityBuyBonusButton',
            payload: {
                opacity: 1,
                isInteractive: true,
            },
        })

        setIsAmuseBoostModalOpen(true)
    }

    const showAmuseBoost = (hasBuyBonus || hasDoubleChance) && !isMultiChoiceToggled && !isDoubleChanceActivated

    if (!hasBuyBonus && !hasDoubleChance) {
        return null
    }

    if (hasBuyBonus && isMultiChoiceToggled) {
        return (
            <>
                <BuyBonusButton onClick={handleBuyBonusButtonClicked} />
                <BuyBonusModal isOpen={showModal} onClose={() => setShowModal(false)} />
            </>
        )
    }

    if (hasDoubleChance && isDoubleChanceActivated) {
        return <DoubleChanceButton />
    }

    if (showAmuseBoost) {
        return (
            <>
                <AmuseBoostButton onClick={handleAmuseBoostClick} />

                <AmuseBoostModal
                    isOpen={isAmuseBoostModalOpen}
                    onClose={() => setIsAmuseBoostModalOpen(false)}
                    hasBuyBonus={hasBuyBonus}
                    hasDoubleChance={hasDoubleChance}
                    onSelectBuyBonus={handleSelectBuyBonusFromAmuseBoost}
                />

                {hasBuyBonus && <BuyBonusModal isOpen={showModal} onClose={() => setShowModal(false)} />}
            </>
        )
    }

    return null
}

export default AmuseBoost
